import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Chip, Modal } from "../components/ui-bits";
import { useBrandPermissions } from "../lib/brand";
import { Plus, CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import { fmtDate, eur } from "../components/sampling/common";
import { LabFields, labPayload } from "../components/productions/LabFields";
import { DeliveryConfirm } from "../components/productions/DeliveryConfirm";
import { LabsSummary } from "../components/productions/LabsSummary";

const STEPS = [["materials", "Materiali arrivati"], ["cutting", "Taglio"], ["sewing", "Confezione"], ["delivery", "Consegna"]];
const TONE = { "In corso": "blue", Completata: "green", Annullata: "red" };
const today = () => new Date().toLocaleDateString("sv-SE");

function StepsBar({ p }) {
  return (
    <div className="flex items-center gap-1" data-testid={`prod-steps-${p.id}`}>
      {p.steps.map(s => {
        const late = p.late_steps.includes(s.key);
        return <span key={s.key} title={`${s.label}${s.actual_date ? ` · fatto ${fmtDate(s.actual_date)}` : s.planned_date ? ` · previsto ${fmtDate(s.planned_date)}` : ""}`}
          className={`h-2 w-10 ${s.actual_date ? "bg-green-600" : late ? "bg-red-500" : s.key === p.current_step ? "bg-orange-400" : ""}`} style={!s.actual_date && !late && s.key !== p.current_step ? { background: "var(--border)" } : {}}/>;
      })}
    </div>
  );
}

function ProductionEditor({ p, canEdit, onClose, onSaved }) {
  const [steps, setSteps] = useState(p.steps.map(s => ({ ...s })));
  const [pieces, setPieces] = useState(p.pieces);
  const [notes, setNotes] = useState(p.notes || "");
  const [lab, setLab] = useState(p.lab || null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const set = (i, patch) => setSteps(ss => ss.map((s, j) => j === i ? { ...s, ...patch } : s));
  const willLoad = (status) => status !== "Annullata" && p.status !== "Annullata" && !p.stock_loaded && steps.find(s => s.key === "delivery").actual_date;
  const save = async (status, delivered) => {
    if (willLoad(status) && !delivered) { setConfirm(true); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.put(`/productions/${p.id}`, { steps: steps.map(({ key, planned_date, actual_date, notes }) => ({ key, planned_date, actual_date, notes })),
        pieces: Number(pieces) || p.pieces, notes, lab: labPayload(lab), ...(status ? { status } : {}), ...(delivered ? { delivered } : {}) });
      onSaved(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); setConfirm(false); }
    setBusy(false);
  };
  return (
    <Modal open onClose={onClose} title={`${p.number} · ${p.garment_code} ${p.garment_name || ""}`} wide>
      <div data-testid="prod-editor">
        <div className="text-xs opacity-70 mb-4">{p.sheet_number ? `Scheda ${p.sheet_number}` : "Senza scheda"}{p.po_numbers?.length ? ` · ordini ${p.po_numbers.join(", ")}` : ""}
          {p.breakdown?.length ? ` · ${p.breakdown.map(b => `${b.size}/${b.color}: ${b.qty}`).join(", ")}` : ""}</div>
        <table className="tbl w-full text-sm [&_td]:!px-2 [&_th]:!px-2">
          <thead><tr><th>Fase</th><th>Prevista</th><th>Effettiva</th><th>Note</th></tr></thead>
          <tbody>{steps.map((s, i) => {
            const late = s.planned_date && !s.actual_date && s.planned_date < today();
            return (
              <tr key={s.key} data-testid={`prod-step-${s.key}`}>
                <td className="whitespace-nowrap">{s.actual_date ? <CheckCircle2 size={14} className="inline text-green-600 mr-2"/> : late ? <AlertTriangle size={14} className="inline text-red-600 mr-2"/> : <Circle size={14} className="inline opacity-40 mr-2"/>}{s.label}</td>
                <td><input disabled={!canEdit} type="date" className="input-dark !py-1" value={s.planned_date || ""} onChange={e => set(i, { planned_date: e.target.value || null })} data-testid={`prod-planned-${s.key}`}/></td>
                <td className="whitespace-nowrap"><input disabled={!canEdit} type="date" className="input-dark !py-1 !w-auto inline-block" value={s.actual_date || ""} onChange={e => set(i, { actual_date: e.target.value || null })} data-testid={`prod-actual-${s.key}`}/>
                  {canEdit && !s.actual_date && <button onClick={() => set(i, { actual_date: today() })} className="text-xs ml-2 underline-offset-4 hover:underline" data-testid={`prod-today-${s.key}`}>Oggi</button>}</td>
                <td><input disabled={!canEdit} className="input-dark !py-1" value={s.notes || ""} onChange={e => set(i, { notes: e.target.value })} data-testid={`prod-notes-${s.key}`}/></td>
              </tr>);
          })}</tbody>
        </table>
        <LabFields lab={lab} setLab={setLab} pieces={pieces} startDate={steps.find(s => s.key === "cutting").planned_date} disabled={!canEdit}/>
        {p.stock_loaded && <div className="text-xs text-green-700 mt-3" data-testid="prod-stock-loaded">Caricata a magazzino prodotti finiti: {(p.delivered || []).map(d => `${d.size || "—"}/${d.color || "—"} ${d.qty}`).join(", ")}</div>}
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <div><label className="label-caps block mb-2">Pezzi</label><input disabled={!canEdit} type="number" min="1" className="input-dark" value={pieces} onChange={e => setPieces(e.target.value)} data-testid="prod-pieces"/></div>
          <div className="md:col-span-2"><label className="label-caps block mb-2">Note</label><input disabled={!canEdit} className="input-dark" value={notes} onChange={e => setNotes(e.target.value)} data-testid="prod-general-notes"/></div>
        </div>
        {err && <div className="text-sm text-red-600 mt-3" data-testid="prod-error">{err}</div>}
        {canEdit && (
          <div className="flex justify-between mt-6">
            {p.status === "Annullata"
              ? <button onClick={() => save("In corso")} className="btn-ghost" data-testid="prod-reopen">Riapri</button>
              : <button onClick={() => window.confirm(`Annullare la produzione ${p.number}?`) && save("Annullata")} className="btn-ghost" data-testid="prod-cancel">Annulla produzione</button>}
            <button disabled={busy} onClick={() => save()} className="btn-gold" data-testid="prod-save">{busy ? "Salvataggio…" : "Salva"}</button>
          </div>
        )}
      </div>
      {confirm && <DeliveryConfirm p={p} pieces={pieces} busy={busy} onClose={() => setConfirm(false)} onConfirm={rows => save(undefined, rows)}/>}
    </Modal>
  );
}

function NewProductionModal({ onClose, onCreated }) {
  const [garments, setGarments] = useState([]);
  const [f, setF] = useState({ garment_id: "", pieces: "", notes: "" });
  const [planned, setPlanned] = useState({});
  const [lab, setLab] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/garments").then(r => setGarments(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setErr("");
    try { const r = await api.post("/productions", { ...f, pieces: Number(f.pieces), planned, lab: labPayload(lab) }); onCreated(r.data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="Nuova produzione">
      <div className="space-y-4" data-testid="prod-new-modal">
        <div><label className="label-caps block mb-2">Capo</label>
          <select className="input-dark" value={f.garment_id} onChange={e => setF({ ...f, garment_id: e.target.value })} data-testid="prod-new-garment">
            <option value="">Scegli…</option>{garments.map(g => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}</select></div>
        <div><label className="label-caps block mb-2">Pezzi</label><input type="number" min="1" className="input-dark" value={f.pieces} onChange={e => setF({ ...f, pieces: e.target.value })} data-testid="prod-new-pieces"/></div>
        <div className="grid grid-cols-2 gap-3">{STEPS.map(([k, l]) => (
          <div key={k}><label className="label-caps block mb-2">{l} · prevista</label><input type="date" className="input-dark" value={planned[k] || ""} onChange={e => setPlanned({ ...planned, [k]: e.target.value })} data-testid={`prod-new-planned-${k}`}/></div>))}</div>
        <LabFields lab={lab} setLab={setLab} pieces={f.pieces} startDate={planned.cutting}/>
        <div><label className="label-caps block mb-2">Note</label><input className="input-dark" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="prod-new-notes"/></div>
        {err && <div className="text-sm text-red-600" data-testid="prod-new-error">{err}</div>}
        <div className="flex justify-end"><button disabled={!f.garment_id || !(Number(f.pieces) > 0)} onClick={save} className="btn-gold" data-testid="prod-new-save">Crea produzione</button></div>
      </div>
    </Modal>
  );
}

export default function ProductionsPage() {
  const nav = useNavigate();
  const { canEdit } = useBrandPermissions();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("In corso");
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("list");
  const load = () => api.get("/productions", { params: { status: status || undefined } }).then(r => setItems(r.data)).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  useEffect(() => {
    const id = params.get("open");
    if (!id) return;
    api.get("/productions").then(r => { const p = r.data.find(x => x.id === id); if (p) setEdit(p); });
  }, [params]);
  const close = () => { setEdit(null); if (params.get("open")) { params.delete("open"); setParams(params, { replace: true }); } };
  const late = (items || []).filter(p => p.is_late).length;
  return (
    <div data-testid="productions-page">
      <PageHeader eyebrow="Produzione" title="Avanzamento produzione" description="Ogni produzione: materiali arrivati, taglio, confezione e consegna, con date previste ed effettive."
        action={canEdit && <button onClick={() => setCreating(true)} className="btn-gold inline-flex items-center gap-2" data-testid="prod-new-btn"><Plus size={14}/> Nuova produzione</button>}/>
      <div className="flex gap-1 border-b mb-6" style={{ borderColor: "var(--border)" }}>
        {[["list", "Produzioni"], ["labs", "Laboratori"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className="px-5 py-3 text-xs uppercase tracking-widest border-b-2" data-testid={`prod-tab-${k}`}
            style={{ color: tab === k ? "var(--text-primary)" : "var(--text-secondary)", borderColor: tab === k ? "var(--text-primary)" : "transparent" }}>{l}</button>))}
      </div>
      {tab === "labs" ? <LabsSummary/> : <>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select className="input-dark !w-auto" value={status} onChange={e => setStatus(e.target.value)} data-testid="prod-filter-status">
          <option value="In corso">In corso</option><option value="Completata">Completate</option><option value="Annullata">Annullate</option><option value="">Tutte</option></select>
        {late > 0 && <span className="text-sm text-red-600 inline-flex items-center gap-2" data-testid="prod-late-count"><AlertTriangle size={14}/> {late} in ritardo</span>}
      </div>
      {!items ? <div className="text-sm opacity-60">Caricamento…</div> : items.length === 0 ?
        <div className="text-sm opacity-60 p-10 border border-dashed text-center" data-testid="prod-empty">Nessuna produzione. Nascono da "Ordina materiali" nella scheda produzione di un capo, oppure creale a mano.</div> : (
        <div className="card-dark overflow-x-auto"><table className="tbl w-full" data-testid="prod-table">
          <thead><tr><th>Numero</th><th>Capo</th><th className="text-right">Pezzi</th><th>Laboratorio</th><th>Fase attuale</th><th>Avanzamento</th><th>Consegna prevista</th><th>Stato</th></tr></thead>
          <tbody>{items.map(p => {
            const del = p.steps.find(s => s.key === "delivery");
            return (
              <tr key={p.id} className="cursor-pointer" onClick={() => setEdit(p)} data-testid={`prod-row-${p.id}`}>
                <td className="font-medium">{p.number}</td>
                <td>{p.garment_deleted ? <span className="opacity-60">{p.garment_code} · capo eliminato</span> : <button onClick={e => { e.stopPropagation(); nav(`/garments/${p.garment_id}/development`); }} className="hover:underline text-left">{p.garment_code} <span className="opacity-60">{p.garment_name}</span></button>}</td>
                <td className="text-right">{p.pieces}</td>
                <td data-testid={`prod-lab-${p.id}`}>{p.lab?.name ? <>{p.lab.name}{p.lab_total ? <div className="text-xs opacity-60">{eur(p.lab_total)}</div> : null}</> : <span className="opacity-40">—</span>}</td>
                <td>{p.current_label}{p.is_late && <div className="text-xs text-red-600">In ritardo: {p.late_labels.join(", ")}</div>}</td>
                <td><StepsBar p={p}/></td>
                <td>{del.actual_date ? `Consegnata ${fmtDate(del.actual_date)}` : fmtDate(del.planned_date)}</td>
                <td><Chip tone={TONE[p.status]}>{p.status}</Chip></td>
              </tr>);
          })}</tbody>
        </table></div>
      )}
      </>}
      {edit && <ProductionEditor p={edit} canEdit={canEdit} onClose={close} onSaved={() => { close(); load(); }}/>}
      {creating && <NewProductionModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); setStatus("In corso"); load(); }}/>}
    </div>
  );
}
